// ABOUTME: The manual STOP control — lets any guest, typing or speaking, cut the agent short with a
// ABOUTME: deterministic button (SPEC-73); voice barge-in stays energy-triggered and is not enough alone.

import { useCallback, useRef } from 'react';
import { flushAgentAudio } from './agentAudioFlush';
import { resetSpeechClock } from './speechClock';
import { transcriptStore } from './transcriptStore';

/** Module-level so the default keeps a stable identity across renders and useCallback deps hold. */
const defaultNow = () => Date.now();

/**
 * How long the discard gate may stay engaged with nothing clearing it.
 *
 * A BACKSTOP, not the normal exit: the gate's normal exit is the stopped turn completing, which the
 * backend always reports (see `noteTurnComplete`). This only fires if that signal is lost — a
 * dropped frame, a socket that dies mid-turn, a model that never closes the turn. 30s is far longer
 * than any tail the backend could still be flushing at us, and far shorter than a guest would
 * tolerate a silent agent.
 */
export const DISCARD_CEILING_MS = 30000;

/**
 * The gate's OTHER length: what a stop engages when no model turn is in flight.
 *
 * The boundary-gated ceiling above is only correct while a turn is actually generating, because a
 * turn boundary is then still to come. When the guest stops a turn that has ALREADY finished — the
 * common case, and the backend says so in as many words ("the COMMON case, because the guest is
 * usually stopping a buffer the client is still playing out", `services/manual_interrupt.py`) —
 * there is no boundary left to arrive, and a 30s gate waiting for one is a 30s mute: a dead
 * microphone and a discarded next answer, with no symptom on screen.
 *
 * Reachable because `isAgentSpeaking` outlives the model by the whole length of the queued audio:
 * it stays true until the end-of-speech timer fires at `((nextStartTime - currentTime) * 1000) +
 * 200` (App.js, the tail of `handleAudioPlayback`), which is seconds after that turn's
 * `turnComplete` was consumed. So `canStop` is live long after the only thing that could lift the
 * ceiling has gone.
 *
 * A short fixed tail instead: long enough to eat the chunks already on the wire when the press
 * landed, far too short to mute anything the guest asks for next.
 */
export const DISCARD_TAIL_MS = 1500;

/**
 * Does this raw ADK event say the turn just ended?
 *
 * The backend relays live events verbatim —
 * `websocket.send_text(event.model_dump_json(exclude_none=True, by_alias=True))`
 * (`services/stream_controller.py`) — so a turn boundary arrives as a field on an untyped event,
 * NOT as a `{type: ...}` message. ADK's `LlmResponse` declares `turn_complete: Optional[bool]` under
 * `alias_generator=to_camel`, so `by_alias=True` puts it on the wire as `turnComplete`, and
 * `exclude_none=True` means it is present only when actually set. Both shapes are checked because
 * the backend's own completion test does the same:
 *
 *     is_complete = getattr(event, "turn_complete", False) or (
 *         getattr(event, "server_content", None) and getattr(event.server_content, "turn_complete", False))
 *
 * Verified against `google-adk==1.23.0` (`google/adk/models/llm_response.py`) and the downstream
 * task's own boundary handling — grep `turn_complete` in `services/stream_controller.py`
 * (`_downstream_task`) rather than trusting a line number; they have already drifted once.
 *
 * Load-bearing on the backend side too: manual-stop suppression there withholds narration but lets
 * `turn_complete` through explicitly ("the frontend's turn bookkeeping depends on it",
 * `services/manual_interrupt.py`). So the one signal this gate needs is the one signal the
 * suppression is documented never to swallow.
 */
function isTurnComplete(msg) {
  if (!msg || typeof msg !== 'object') return false;
  return msg.turnComplete === true || msg.serverContent?.turnComplete === true;
}

/**
 * ─── Why a discard gate exists at all ───────────────────────────────────────────────────────────
 *
 * Stopping the queued audio is not enough. The backend does not know the guest pressed stop until
 * our `{type:'stop'}` reaches it, so chunks generated before that are already on the wire, and each
 * lands in `handleAudioPlayback` whose FIRST act is `isAgentSpeakingRef.current = true` +
 * `setAgentState('speaking')` — the Cavalry cluster snaps straight back to the speaking shape and a
 * fresh tail of speech plays. The gate is the synchronous flag that path reads to drop them.
 *
 * ─── The gate covers CAPTIONS too, not just audio ───────────────────────────────────────────────
 *
 * Ophion runs `gemini-live-2.5-flash-native-audio`, where the agent's words reach the client as
 * `transcript` messages on a completely separate branch of `ws.onmessage` from the binary audio —
 * and the backend's own suppression only catches words that arrive as content *parts*, which these
 * are not. Gating audio alone therefore produced the worst possible read: the voice cuts out and
 * the caption bar carries on printing the rest of the answer, and then the sentences after it. So
 * `shouldDiscardAgentOutput()` guards both paths — the AGENT's transcript only, since the guest's
 * captions are their own words.
 *
 * The freeze that leaves behind reads correctly only because two things happen together: the
 * flush's `resetSpeechClock()` drops the pacing denominator so `speechProgress()` returns null and
 * the caption reveals its full current text rather than stalling half-drawn, AND `stop()` re-pushes
 * that text with `isPartial: false`. Without the settle, `transcriptStore.latest` keeps
 * `isPartial: true` forever, so `useCaptionVisible()` stays true and the reel's editorial caption
 * stays suppressed until the guest's next turn — the "pinned forever" failure.
 *
 * ─── It also gates the MICROPHONE, which is a cross-repo contract ───────────────────────────────
 *
 * `App.js handleMicFrame` refuses to stream PCM while this gate is engaged, and that is not a local
 * nicety — it is the precondition the BACKEND's own suppression is written against.
 * `services/manual_interrupt.py` treats an inbound BINARY frame as condition B of its release
 * ("the guest re-engaged"), taken on the upstream task before the frame reaches the queue. Read
 * that module's state machine rather than paraphrasing it: `release()` lifts on the spot when the
 * stopped turn had already finished at press time (`turn_open` False — its stated common case),
 * and only banks the release for `note_turn_complete()` when the turn was still generating. So in
 * the common case ONE stray frame ends the server-side stop. The mic emits a frame every ~128ms,
 * so a client that kept streaming would lift that stop within a frame period of setting it and the
 * backend half would be inert. Gating the mic here is what makes "a frame arrived" genuinely mean
 * "the CLIENT decided the guest is speaking" — the only reading under which the backend's release
 * rule is correct. Do not remove one side without the other.
 *
 * Same argument, same reason, for the VAD's `speech_start` / `speech_end` markers: they are typed
 * messages, `releases_on` is a fail-OPEN denylist and neither is in its telemetry set, so either
 * one releases the backend's stop as surely as a mic frame does. They are energy-triggered, which
 * makes them exactly the "room noise un-stops the agent" hazard this gate exists to close, and
 * with `MANUAL_ACTIVITY_DETECTION=false` in every shipped environment the backend cannot act on
 * them anyway. App.js therefore withholds them behind `shouldDiscardAgentOutput()` too.
 *
 * ─── The gate's LIFETIME: two lengths, chosen by whether a turn is in flight ─────────────────────
 *
 * The gate's only job is to swallow the tail of the turn the guest abandoned. Once that turn is
 * complete there is nothing left to suppress, and anything the agent says afterwards belongs to a
 * NEW turn the guest caused — which must be heard.
 *
 * So: when a model turn IS in flight at press time, the gate is boundary-gated — it lifts on that
 * turn's `turn_complete`, or on `DISCARD_CEILING_MS`, whichever lands first. When one is NOT (the
 * guest is stopping audio the client is still playing out of a turn the model finished long ago),
 * no boundary is coming and waiting for one would mute the session; the gate takes the short fixed
 * `DISCARD_TAIL_MS` instead. `turnInFlightRef` is the frontend's own copy of the backend's
 * `manual_stop_turn_open`, and it is fed the same way: set by any agent content event, cleared by
 * `turn_complete`.
 *
 * A turn the guest has just COMMITTED counts as in flight even before its first token, which is
 * why `agentState === 'thinking'` is folded in at the press. Otherwise a guest who types and
 * immediately changes their mind gets the short tail, and the answer they cancelled arrives just
 * after it expires and plays in full.
 *
 * Neither length lifts on guest input, which is what the gate used to do and was wrong twice over:
 *
 *   - the abandoned tail could RESUME. Guest presses stop, then taps a product card half a second
 *     later while the same turn is still flushing at us: gate opens, the rest of the speech the
 *     guest asked to be rid of plays out on top of the card they just tapped.
 *   - ROOM NOISE could open it. The VAD's `onSpeechStart` is energy-triggered, so a companion
 *     talking, a door, or a laptop mic hearing its own speakers re-opened the gate for free.
 *
 * Note the second one takes BOTH halves to actually be closed, and for a while only one of them
 * was. This gate no longer lifts on `onSpeechStart`, but the callback still put `speech_start` on
 * the wire, and that message releases the stop on the SERVER — so the room could not un-mute the
 * client and could un-mute the backend, which is the same bug wearing a different hat. The marker
 * gating described above is the other half; neither is sufficient alone.
 *
 * ─── Why the gate still FAILS OPEN ──────────────────────────────────────────────────────────────
 *
 * The two ways of getting the lifetime wrong are not equally bad:
 *
 *   Cleared too EARLY  → the guest hears a second of the tail they asked to be rid of. Cosmetic.
 *   Cleared too LATE   → every later turn is silently discarded. The agent is MUTE, with no
 *                        symptom to read and no recovery short of a page refresh. Catastrophic.
 *
 * `turn_complete` is a reliable signal, but "reliable" is not "guaranteed", so `DISCARD_CEILING_MS`
 * remains as the unconditional backstop. It is enforced lazily on read rather than with a
 * `setTimeout`: no timer to leak, nothing to clean up on unmount, and — given the repo's documented
 * fake-timers-clobber-rAF problem — nothing that forces a test into fake timers. Tests drive it by
 * injecting `now`.
 *
 * One accepted imprecision, on the cosmetic side of that trade: native audio is documented to emit
 * a spurious EMPTY `turn_complete` as a boundary before it starts a tool turn (grep
 * `empty_turn_count` in `services/stream_controller.py` for the counting that exists because of
 * it). If one of those lands between the stop and the real end of the turn, the gate opens early
 * and a little tail is heard. That is the failure direction we chose.
 *
 * @param {object}   p
 * @param {boolean}  p.enabled                 tenant flag `feature_flags.manual_interrupt` (Ophion)
 * @param {boolean}  p.isAgentSpeaking         React mirror of `isAgentSpeakingRef`
 * @param {string}   p.agentState              'thinking' is stoppable too — a turn being composed
 *                                             — and it is read a second time at the press, as the
 *                                             "a turn IS in flight" case with no content yet
 * @param {object}   p.wsRef                   ref holding the agent socket
 * @param {object}   p.audioContext            the four playback-graph refs, handed straight to
 * @param {object}   p.activeAudioSourcesRef   `flushAgentAudio`; this hook never reads them itself,
 * @param {object}   p.nextStartTime           which is what keeps the flush independently testable
 * @param {object}   p.speakingTimeoutRef
 * @param {object}   p.isAgentSpeakingRef      the ref the audio path and the mic gate both read
 * @param {Function} p.setIsAgentSpeaking      the three React mirrors the flush returns to rest
 * @param {Function} p.setIsUserSpeaking
 * @param {Function} p.setAgentState
 * @param {Function} [p.setLiveAgentText]      the four the end-of-speech timer would have cleared;
 * @param {Function} [p.setHighlightedFeatures] a stop cancels that timer, so it has to do its work
 * @param {Function} [p.setActiveLoreImage]     itself or the abandoned turn's UI lingers
 * @param {object}   [p.highlightedThisTurnRef]
 * @param {Function} [p.now]                   injectable clock, for the ceiling's tests
 */
export function useManualStop({
  enabled = false,
  isAgentSpeaking = false,
  agentState = 'listening',
  wsRef,
  audioContext,
  activeAudioSourcesRef,
  nextStartTime,
  speakingTimeoutRef,
  isAgentSpeakingRef,
  setIsAgentSpeaking,
  setIsUserSpeaking,
  setAgentState,
  setLiveAgentText,
  setHighlightedFeatures,
  setActiveLoreImage,
  highlightedThisTurnRef,
  now = defaultNow,
} = {}) {
  // Deadline (ms, in `now`'s clock) up to which agent audio is thrown away; 0 means "gate open".
  // A deadline rather than a boolean so the ceiling needs no timer — see the note above.
  const discardRef = useRef(0);

  // The frontend's copy of the backend's `manual_stop_turn_open`: is the MODEL still generating?
  // Not the same question as "is the guest still hearing the agent" — `isAgentSpeaking` answers
  // that one and stays true for the whole length of the locally queued audio, long after the turn
  // that produced it closed. Only this ref can tell `stop()` whether a `turn_complete` is still
  // coming, and therefore whether the gate may wait for one.
  const turnInFlightRef = useRef(false);

  // Rendered whenever the tenant has the flag, in voice mode as well as keyboard mode (SPEC-73). It
  // used to be keyboard-only, on the argument that a voice guest can already barge in. Owner ruling
  // 2026-09-28 (Miguel's Ophion session): barge-in is energy-triggered, so it is unreliable in a real
  // room — sometimes it will not fire, sometimes room chatter fires it (~25 false interrupts in his
  // session) — and the guest needs one control that always works. Keyboard mode still cannot barge
  // in by voice at all (see the rising-edge branch in App.js `useVoiceActivityDetection`), which is
  // why the button was born there; that reason stands, it is just no longer the only one.
  const showStop = Boolean(enabled);

  // 'thinking' counts. That turn has produced no audio yet, but it is the state a typing guest is
  // most often in when they change their mind, and a stop that no-ops there reads as broken.
  const canStop = showStop && (Boolean(isAgentSpeaking) || agentState === 'thinking');

  /**
   * Read by `handleAudioPlayback`, the agent-transcript branch of `ws.onmessage`, `handleMicFrame`
   * and the VAD's activity markers, before any of them does anything else. Self-clears once its
   * deadline has passed — whichever of the two lengths was engaged — so an expired gate can never
   * survive into the next turn.
   */
  const shouldDiscardAgentOutput = useCallback(() => {
    const until = discardRef.current;
    if (!until) return false;
    if (now() >= until) {
      discardRef.current = 0;
      return false;
    }
    return true;
  }, [now]);

  /**
   * The gate's normal exit. Fed EVERY parsed message from `ws.onmessage`; lifts the gate on the one
   * that closes the abandoned turn.
   *
   * Call it AFTER that event's own content has been handled, never before — native audio can attach
   * a trailing audio part to the very event carrying `turnComplete`, and that part is still the
   * abandoned turn's. Handling first, lifting second, is what swallows it.
   *
   * @returns {boolean} whether this message was the turn boundary (for tests / callers that care)
   */
  const noteTurnComplete = useCallback((msg) => {
    if (!isTurnComplete(msg)) return false;
    turnInFlightRef.current = false;
    discardRef.current = 0;
    return true;
  }, []);

  /**
   * "The model is generating." Fed from every branch of `ws.onmessage` that carries agent content —
   * a binary audio frame, an agent `transcript`, a `modelTurn` with parts — and cleared again by
   * `noteTurnComplete`. Between those two the client knows a boundary is still to come, which is
   * the one fact `stop()` needs to decide how long the gate may stay shut.
   *
   * Deliberately called even while the gate is engaged: content arriving during a stop means the
   * abandoned turn is still streaming, which is precisely a turn in flight. And content arriving
   * after a boundary belongs to a NEW turn, so re-arming there is right too — the backend emits a
   * turn's parts before its `turn_complete`, never after (bar the one part that can ride ON the
   * boundary event, which `ws.onmessage` handles before lifting, so this clears last and wins).
   */
  const noteAgentActivity = useCallback(() => {
    turnInFlightRef.current = true;
  }, []);

  /**
   * A socket died, or a new one opened. Drop the gate on the floor.
   *
   * `discardRef` lives on the App instance and outlives the WebSocket, and SPEC-11 reconnects on a
   * 1→2→4→8→15s backoff — every one of those well inside `DISCARD_CEILING_MS`. Without this a stop
   * pressed shortly before a transport blip resumes into the FRESH session: its welcome audio and
   * captions are discarded and the microphone stays dead until the ceiling expires. There is no
   * abandoned tail on a new session by definition — the turn that was stopped died with the socket
   * that was carrying it — so there is nothing left for the gate to be protecting.
   */
  const resetForNewSession = useCallback(() => {
    discardRef.current = 0;
    turnInFlightRef.current = false;
  }, []);

  const sendStop = useCallback(() => {
    const ws = wsRef?.current;
    // 1 === WebSocket.OPEN, spelled out because jsdom test doubles do not always carry the constant.
    if (!ws || ws.readyState !== 1) return;
    try {
      ws.send(JSON.stringify({ type: 'stop' }));
    } catch (e) {
      // A dying socket must not take the local flush with it — silence has already been delivered.
    }
  }, [wsRef]);

  /**
   * Kill the queued audio and put the UI back at rest. Shared by both stop paths.
   *
   * The second half is not decoration. `flushAgentAudio` clears AND nulls `speakingTimeoutRef`, and
   * that timer's callback (App.js, at the end of `handleAudioPlayback`) is what normally tears the
   * turn's UI down: the live subtitle, the highlighted spec features, the per-turn highlight ledger
   * and the lore image. Cancelling it without doing its work leaves the abandoned turn's
   * decorations on screen over a silent agent, which reads as a half-applied stop.
   */
  const flushAndRest = useCallback(() => {
    const stopped = flushAgentAudio({
      audioContext,
      activeAudioSourcesRef,
      nextStartTime,
      speakingTimeoutRef,
      resetSpeechClock,
    });
    if (isAgentSpeakingRef) isAgentSpeakingRef.current = false;
    setIsAgentSpeaking?.(false);
    setIsUserSpeaking?.(false);
    // 'listening' is the transport's resting value; `resolveVisualizerState` maps it to 'idle',
    // which CavalryViz aliases onto the authored "Idle - Waiting" shape. Do NOT invent a new state
    // string for this — voiceVisualizerState.test.js and ParticlePill.test.js are totality guards.
    setAgentState?.('listening');

    // The rest of the cancelled end-of-speech teardown. Order is immaterial — they are four
    // independent resets — but the SET has to match the timer's exactly, or a stop leaves behind
    // whichever one was forgotten.
    setLiveAgentText?.('');
    setHighlightedFeatures?.([]);
    if (highlightedThisTurnRef?.current?.clear) highlightedThisTurnRef.current.clear();
    setActiveLoreImage?.(null);
    return stopped;
  }, [
    audioContext, activeAudioSourcesRef, nextStartTime, speakingTimeoutRef,
    isAgentSpeakingRef, setIsAgentSpeaking, setIsUserSpeaking, setAgentState,
    setLiveAgentText, setHighlightedFeatures, setActiveLoreImage, highlightedThisTurnRef,
  ]);

  /**
   * Settle the agent's in-flight partial so `useCaptionVisible()` can go false — see the caption
   * note at the top of the file.
   *
   * `isPartial` is the first condition, and it is load-bearing on its own: an already-settled line
   * must be left at its exact object identity, because re-pushing it would re-render every
   * subscriber for nothing.
   *
   * The second condition is a question of PROVENANCE, and it needs two answers because
   * `peek('agent')` is SENDER-scoped — it hands back the agent's last line whatever turn it came
   * from. On a grid tenant, or any tenant where a turn dies without its final, a partial from an
   * earlier turn can sit there indefinitely, and re-pushing it would resurrect a stale caption as
   * the newest thing on screen. So one of these has to hold:
   *
   *   - the agent's partial IS the store's overall latest — nothing has been said since, so it can
   *     only belong to the turn being stopped; or
   *   - a turn is in flight, which says the same thing from the other side: agent content has
   *     arrived and no boundary has closed it, so the agent's newest partial is THIS turn's.
   *
   * The second clause is not belt-and-braces, it is the fix for a live bug. `handleTextSubmit`
   * pushes the guest's line into the same store, so a guest who types a second message while an
   * answer is being spoken makes the USER's line the latest — the identity test then fails, the
   * settle is skipped, and the gate goes on to swallow the backend's real `isPartial: false`
   * final. `useCaptionVisible()` stays true for the rest of the session and the reel's editorial
   * caption is suppressed behind an agent caption that will never be finished.
   *
   * @param {boolean} turnInFlight `stop()`'s reading of `turnInFlightRef` at press time
   */
  const settleAgentCaption = useCallback((turnInFlight = false) => {
    const line = transcriptStore.peek('agent');
    if (!line?.isPartial) return false;
    if (transcriptStore.peek() !== line && !turnInFlight) return false;
    transcriptStore.push({ sender: 'agent', text: line.text, isPartial: false });
    return true;
  }, []);

  /**
   * The button. Silences the agent, tells the backend to abandon the turn, and holds the gate shut
   * against whatever is still in flight.
   *
   * How long it holds depends on whether there is a boundary left to wait for — see the LIFETIME
   * note above. A turn actually generating gets the boundary-gated ceiling; a turn the model
   * already finished (the guest is stopping queued audio) gets the short tail, because no
   * `turn_complete` is coming and a gate waiting for one is a silently mute session.
   *
   * @returns {boolean} whether it actually stopped anything (false = nothing was stoppable)
   */
  const stop = useCallback(() => {
    if (!canStop) return false;
    // 'thinking' is a committed guest turn whose first token has not landed yet: no content has
    // arrived, so the ref is false, but a boundary IS coming and the answer must be gated.
    const turnInFlight = turnInFlightRef.current || agentState === 'thinking';
    // Engaged BEFORE the flush so there is no window, however theoretical, in which a chunk is
    // processed between the two and re-arms the speaking flags the flush is about to clear.
    discardRef.current = now() + (turnInFlight ? DISCARD_CEILING_MS : DISCARD_TAIL_MS);
    flushAndRest();
    settleAgentCaption(turnInFlight);
    sendStop();
    return true;
  }, [canStop, agentState, now, flushAndRest, settleAgentCaption, sendStop]);

  /**
   * Submitting text auto-stops a speaking agent: the guest has plainly moved on, and making them
   * press stop first is a step nobody would think to take.
   *
   * Unlike `stop()` this does NOT engage the gate, which is the whole point. A reply to the message
   * being sent is due within a second or two, and the two are indistinguishable at the audio path,
   * so gating here risks muting the very answer the guest just asked for. Fail open: flush, accept a
   * possible half-second of tail, stay audible.
   *
   * It does not lift an already-engaged gate either — nothing does, except the turn ending or the
   * deadline passing. If the guest pressed stop and then typed, what opens the gate depends on
   * which length the stop took:
   *
   *   - stopped a turn IN FLIGHT → the boundary. The backend serialises turns (`_await_turn_idle`
   *     in `stream_controller.py`), so it cannot start the new turn before the old one closes and
   *     the boundary genuinely does land ahead of this message's reply.
   *   - stopped a turn already FINISHED → `DISCARD_TAIL_MS`, which is shorter than any round trip.
   *     There is no boundary to wait for here, and claiming there was is what made the gate able
   *     to swallow a whole answer the guest had asked for.
   */
  const interruptForGuestTurn = useCallback(() => {
    if (!enabled) return false;
    if (!isAgentSpeakingRef?.current) return false;
    flushAndRest();
    sendStop();
    return true;
  }, [enabled, isAgentSpeakingRef, flushAndRest, sendStop]);

  // FOLLOW-UP (reviewer, 2026-09-01): the `{type:'stop'}` frame here and the ~dozen other
  // `ws.current.send(JSON.stringify(...))` sites in App.js would read better behind one
  // `sendToAgent(msg)` choke point. Deliberately NOT done in this change: every one of those sites
  // is inside the WebSocket lifecycle, which CLAUDE.md locks, and rewiring it alongside a new
  // feature is exactly how the reconnect path gets broken. Its own change, on its own.
  return {
    showStop, canStop, stop, interruptForGuestTurn,
    noteTurnComplete, noteAgentActivity, resetForNewSession,
    shouldDiscardAgentOutput, discardRef, turnInFlightRef,
  };
}

export default useManualStop;
